import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Package, Calculator, DollarSign, Settings as SettingsIcon, Plus, AlertTriangle, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { countWord } from '../lib/countWord.mjs';
import { searchNeedle, matchesSearch } from '../lib/listSearch.mjs';
import Modal from '../components/ui/Modal';

const usd = (n, dp = 2) => (n == null || Number.isNaN(Number(n)) ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const fmt = (n, dp = 0) => (n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const input = 'w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';
const btn = 'rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50';
const btn2 = 'rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium disabled:opacity-50';

const TABS = [
  ['products', 'Products & costs', Package],
  ['calc', 'Calculators', Calculator],
  ['fx', 'FX rates', DollarSign],
  ['settings', 'Pricing rules', SettingsIcon],
];

export default function CatalogPage() {
  const [tab, setTab] = useState('products');
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/catalog/meta').then(setMeta).catch((e) => setError(e.message)); }, []);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Package size={20} /> Catalog & pricing</h1>
        <p className="text-sm text-[var(--text-secondary)]">Concria products and codes, dated costs (EUR/USD), landed cost, coverage and sell-price rules.</p>
      </div>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-[var(--border)]">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-[var(--text-secondary)]'}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>
      {error && <Err msg={error} />}
      {meta && tab === 'products' && <ProductsTab meta={meta} />}
      {meta && tab === 'calc' && <CalcTab meta={meta} />}
      {meta && tab === 'fx' && <FxTab />}
      {meta && tab === 'settings' && <SettingsTab />}
    </div>
  );
}

function Err({ msg }) {
  return msg ? <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div> : null;
}

function CostChip({ cost }) {
  if (!cost || cost.status !== 'known') return <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700" title={cost?.reason}>UNKNOWN</span>;
  const prov = cost.version?.status === 'provisional';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${prov ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}
      title={cost.warnings?.join(' ')}>{prov ? 'provisional' : 'confirmed'}</span>
  );
}

function costPer(cost) {
  if (!cost || cost.status !== 'known') return '—';
  if (cost.usd_per_lb != null) return `${usd(cost.usd_per_lb, 4)}/lb`;
  if (cost.usd_per_gallon != null) return `${usd(cost.usd_per_gallon)}/gal`;
  if (cost.usd_per_each != null) return `${usd(cost.usd_per_each)}/ea`;
  return '—';
}

// ── Products ──────────────────────────────────────────────────────────────────
function ProductsTab({ meta }) {
  const [rows, setRows] = useState([]);
  const [family, setFamily] = useState('');
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/catalog/products?active=${showAll ? 'all' : 'true'}${family ? `&family=${family}` : ''}`);
      setRows(r.products || []); setError('');
    } catch (e) { setError(e.message); }
  }, [family, showAll]);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => {
    // Use the shared normalized search so trailing spaces do not hide matches.
    const needle = searchNeedle(q);
    return rows.filter((p) => matchesSearch(needle, [[p.sku, p.name, p.colour, p.concria_item_code].filter(Boolean).join(' ')]));
  }, [rows, q]);
  const unknown = rows.filter((p) => p.cost?.status !== 'known').length;

  return (
    <div>
      <Err msg={error} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input className={`${input} max-w-xs`} placeholder="Search SKU, name, colour" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={`${input} max-w-[12rem]`} value={family} onChange={(e) => setFamily(e.target.value)}>
          <option value="">All families</option>
          {meta.families.map((f) => <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs text-[var(--text-secondary)]"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> inactive too</label>
        <div className="flex-1" />
        <button className={btn} onClick={() => setCreating(true)}><Plus size={14} className="mr-1 inline" />Product</button>
      </div>
      {unknown > 0 && (
        <p className="mb-3 flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {unknown} of {rows.length} products have no usable CCS cost. The 2026 MSRP list (Ben, 4 Aug 2026) is not loaded yet; add it per product as a cost version, and add today's EUR→USD rate.
        </p>
      )}
      <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[var(--text-secondary)]">
            <th className="px-3 py-2">SKU</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Unit</th>
            <th className="px-3 py-2 text-right">CCS cost / unit</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2">Basis</th>
          </tr></thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} onClick={() => setOpen(p.id)} className="cursor-pointer border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800">
                <td className="px-3 py-2 font-mono text-xs">{p.sku}{p.code_status === 'provisional' && <span className="ml-1 text-amber-600" title="Not a Concria code yet">*</span>}</td>
                <td className="px-3 py-2">{p.name}{!p.active && <span className="ml-1 text-xs text-[var(--text-secondary)]">(inactive)</span>}</td>
                <td className="px-3 py-2 text-xs">{p.sale_unit}{p.unit_qty ? ` · ${fmt(p.unit_qty, 2)} ${p.unit_qty_uom}` : ''}</td>
                <td className="px-3 py-2 text-right">{p.cost?.status === 'known' ? usd(p.cost.usd_per_unit) : '—'}</td>
                <td className="px-3 py-2 text-right text-xs">{costPer(p.cost)}</td>
                <td className="px-3 py-2"><CostChip cost={p.cost} /></td>
              </tr>
            ))}
            {!shown.length && !error && <tr><td colSpan={6} className="p-6 text-center text-[var(--text-secondary)]">{rows.length && searchNeedle(q) ? 'No products match.' : 'No products. Run the catalog seed, or add one.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">* SKU is a placeholder until Concria confirms its code (Chase asked for the code list on 21 Oct 2025).</p>
      {open && <ProductModal id={open} meta={meta} onClose={() => { setOpen(null); load(); }} />}
      {creating && <ProductForm meta={meta} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ProductModal({ id, meta, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [edit, setEdit] = useState(false);
  const [addCost, setAddCost] = useState(false);
  const load = useCallback(() => api.get(`/catalog/products/${id}`).then(setData).catch((e) => setError(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  async function del(cid) {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this cost version?')) return;
    try { await api.delete(`/catalog/costs/${cid}`); load(); } catch (e) { setError(e.message); }
  }
  if (!data) return <Modal onClose={onClose} title="Product"><Err msg={error} />Loading…</Modal>;
  const p = data.product;
  return (
    <Modal onClose={onClose} title={`${p.sku} · ${p.name}`} maxWidth="max-w-3xl">
      <Err msg={error} />
      <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
        <Field label="Family" value={`${p.family}${p.grade ? ` · ${p.grade}` : ''}`} />
        <Field label="Concria item / design code" value={[p.concria_item_code, p.design_code].filter(Boolean).join(' / ') || '—'} />
        <Field label="Sale unit" value={`${p.sale_unit}${p.unit_qty ? ` · ${p.unit_qty} ${p.unit_qty_uom}` : ''}`} />
        <Field label="Weight / pallet" value={`${p.unit_weight_lb ? `${p.unit_weight_lb} lb` : '—'} · ${p.units_per_pallet ? `${p.units_per_pallet}/pallet` : '—'}`} />
        <Field label="Floor" value={p.floor_price_usd != null ? `${usd(p.floor_price_usd, 4)} ${p.floor_basis}` : 'not set'} />
        <Field label="Cost today" value={data.cost.status === 'known' ? `${usd(data.cost.usd_per_unit)} / ${p.sale_unit}` : `UNKNOWN: ${data.cost.reason}`} />
      </div>
      {data.cost.warnings?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-amber-700">{data.cost.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
      {p.data_gaps?.length > 0 && <p className="mt-2 text-xs text-[var(--text-secondary)]"><b>Unknown:</b> {p.data_gaps.join('; ')}</p>}
      {p.notes && <p className="mt-2 whitespace-pre-wrap text-xs text-[var(--text-secondary)]">{p.notes}</p>}
      {p.source && <p className="mt-1 text-[11px] text-[var(--text-secondary)]"><b>Source:</b> {p.source}</p>}
      <div className="mt-3 flex gap-2">
        <button className={btn2} onClick={() => setEdit(true)}>Edit product</button>
        <button className={btn2} onClick={() => setAddCost(true)}>Add cost version</button>
      </div>
      <h3 className="mb-1 mt-4 text-sm font-semibold">Cost versions</h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-xs">
          <thead><tr className="text-left text-[var(--text-secondary)]"><th className="py-1">Effective</th><th>Type</th><th>Price</th><th>Break</th><th>Discount</th><th>Status</th><th /></tr></thead>
          <tbody>
            {data.versions.map((v) => (
              <tr key={v.id} className="border-t border-[var(--border)] align-top">
                <td className="py-1">{v.effective_date}<div className="text-[var(--text-secondary)]">{v.label}</div></td>
                <td>{v.price_type.replace('_', ' ')}</td>
                <td>{v.list_price == null ? 'UNKNOWN' : `${v.currency} ${v.list_price} ${v.price_basis.replace('_', '/')}`}</td>
                <td>{v.break_qty_kg ? `<${fmt(v.break_qty_kg)} kg: ${v.small_order_price ?? (v.small_order_uplift_pct != null ? `+${v.small_order_uplift_pct}%` : '—')}` : '—'}</td>
                <td>{Number(v.discount_pct) ? `${v.discount_pct}%` : '—'}<div className="text-[var(--text-secondary)]">{v.discount_rule}</div></td>
                <td>{v.status}</td>
                <td><button onClick={() => del(v.id)} className="text-red-600" aria-label="Delete"><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {!data.versions.length && <tr><td colSpan={7} className="py-2 text-[var(--text-secondary)]">No cost versions: cost is UNKNOWN.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <ProductForm meta={meta} product={p} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
      {addCost && <CostForm meta={meta} productId={p.id} onClose={() => setAddCost(false)} onSaved={() => { setAddCost(false); load(); }} />}
    </Modal>
  );
}

function Field({ label, value }) {
  return <div className="min-w-0"><p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</p><p className="break-words">{value}</p></div>;
}

function clean(obj, original) {
  // Only send changed keys on edit; '' clears a field.
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (!original || String(original[k] ?? '') !== String(v ?? '')) out[k] = v;
  return out;
}

function ProductForm({ meta, product, onClose, onSaved }) {
  const init = product || { sku: '', name: '', family: 'dry_shake', sale_unit: 'bag', unit_qty: '', unit_qty_uom: '', unit_weight_lb: '', units_per_pallet: '', floor_price_usd: '', floor_basis: '', notes: '' };
  const keys = ['sku', 'name', 'family', 'grade', 'colour', 'concria_item_code', 'design_code', 'sale_unit', 'unit_qty', 'unit_qty_uom', 'unit_weight_lb', 'units_per_pallet', 'default_dosage_lb_sf', 'coverage_sf_per_gal', 'floor_price_usd', 'floor_basis', 'notes'];
  const [f, setF] = useState(Object.fromEntries(keys.map((k) => [k, init[k] ?? ''])));
  const [active, setActive] = useState(product ? product.active : true);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save(e) {
    e.preventDefault();
    try {
      const body = clean(f, product ? Object.fromEntries(keys.map((k) => [k, product[k] ?? ''])) : null);
      if (!product) for (const k of Object.keys(body)) if (body[k] === '') delete body[k];
      if (product && active !== product.active) body.active = active;
      if (product) await api.patch(`/catalog/products/${product.id}`, body); else await api.post('/catalog/products', body);
      onSaved();
    } catch (e2) { setErr(e2.message); }
  }
  const sel = (k, opts, blank = true) => (
    <select className={input} value={f[k]} onChange={set(k)}>{blank && <option value="">—</option>}{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
  );
  return (
    <Modal onClose={onClose} title={product ? `Edit ${product.sku}` : 'New product'}>
      <form onSubmit={save} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <L t="SKU (Concria code where known)"><input className={input} value={f.sku} onChange={set('sku')} required /></L>
        <L t="Name"><input className={input} value={f.name} onChange={set('name')} required /></L>
        <L t="Family">{sel('family', meta.families, false)}</L>
        <L t="Grade">{sel('grade', ['industrial', 'deco'])}</L>
        <L t="Colour"><input className={input} value={f.colour} onChange={set('colour')} /></L>
        <L t="Concria item code / design code"><div className="flex gap-2"><input className={input} value={f.concria_item_code} onChange={set('concria_item_code')} /><input className={input} value={f.design_code} onChange={set('design_code')} /></div></L>
        <L t="Sale unit">{sel('sale_unit', meta.sale_units, false)}</L>
        <L t="Pack size + unit"><div className="flex gap-2"><input className={input} inputMode="decimal" value={f.unit_qty} onChange={set('unit_qty')} />{sel('unit_qty_uom', meta.uoms)}</div></L>
        <L t="Gross weight per unit (lb)"><input className={input} inputMode="decimal" value={f.unit_weight_lb} onChange={set('unit_weight_lb')} /></L>
        <L t="Units per pallet"><input className={input} inputMode="numeric" value={f.units_per_pallet} onChange={set('units_per_pallet')} /></L>
        <L t="Default dosage (lb/sf)"><input className={input} inputMode="decimal" value={f.default_dosage_lb_sf} onChange={set('default_dosage_lb_sf')} /></L>
        <L t="Coverage (sf/gal)"><input className={input} inputMode="decimal" value={f.coverage_sf_per_gal} onChange={set('coverage_sf_per_gal')} /></L>
        <L t="Floor price (USD) + basis"><div className="flex gap-2"><input className={input} inputMode="decimal" value={f.floor_price_usd} onChange={set('floor_price_usd')} />{sel('floor_basis', meta.floor_bases)}</div></L>
        {product && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active</label>}
        <L t="Notes" wide><textarea className={input} rows={2} value={f.notes} onChange={set('notes')} /></L>
        <div className="sm:col-span-2"><Err msg={err} /><button className={btn} type="submit">Save</button></div>
      </form>
    </Modal>
  );
}

function L({ t, children, wide }) {
  return <label className={`block text-xs text-[var(--text-secondary)] ${wide ? 'sm:col-span-2' : ''}`}><span className="mb-1 block">{t}</span>{children}</label>;
}

function CostForm({ meta, productId, onClose, onSaved }) {
  // Discount starts blank: it must be entered for the product (tooling's is unknown, for one).
  const [f, setF] = useState({ effective_date: new Date().toISOString().slice(0, 10), price_type: 'supplier_list', currency: 'EUR', price_basis: 'per_kg', list_price: '', discount_pct: '', discount_rule: '', break_qty_kg: '', small_order_price: '', status: 'provisional', label: 'CCS cost', source: '' });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save(e) {
    e.preventDefault();
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      if (f.price_type !== 'supplier_list') { body.discount_pct = 0; delete body.discount_rule; }
      await api.post(`/catalog/products/${productId}/costs`, body);
      onSaved();
    } catch (e2) { setErr(e2.message); }
  }
  const sel = (k, opts) => <select className={input} value={f[k]} onChange={set(k)}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  return (
    <Modal onClose={onClose} title="Add cost version">
      <form onSubmit={save} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <L t="Effective date"><input type="date" className={input} value={f.effective_date} onChange={set('effective_date')} required /></L>
        <L t="Type"><select className={input} value={f.price_type} onChange={(e) => setF({ ...f, price_type: e.target.value, ...(e.target.value === 'supplier_list' ? {} : { discount_pct: '0', discount_rule: '' }) })}>
          {meta.price_types.map((o) => <option key={o} value={o}>{o}</option>)}
        </select></L>
        <L t="Currency / basis"><div className="flex gap-2">{sel('currency', meta.currencies)}{sel('price_basis', meta.price_bases)}</div></L>
        <L t="List price (blank = UNKNOWN)"><input className={input} inputMode="decimal" value={f.list_price} onChange={set('list_price')} /></L>
        {f.price_type === 'supplier_list' && <L t="Discount % (required with a list price) and rule"><div className="flex gap-2"><input className={`${input} w-20`} inputMode="decimal" placeholder="e.g. 65" value={f.discount_pct} onChange={set('discount_pct')} /><input className={input} placeholder="e.g. 2026 MSRP − 65%" value={f.discount_rule} onChange={set('discount_rule')} /></div></L>}
        <L t="3-pallet break (kg) / price under it"><div className="flex gap-2"><input className={input} inputMode="decimal" placeholder="3200" value={f.break_qty_kg} onChange={set('break_qty_kg')} /><input className={input} inputMode="decimal" value={f.small_order_price} onChange={set('small_order_price')} /></div></L>
        <L t="Status">{sel('status', ['provisional', 'confirmed'])}</L>
        <L t="Label"><input className={input} value={f.label} onChange={set('label')} /></L>
        <L t="Source (document, email id)" wide><input className={input} value={f.source} onChange={set('source')} /></L>
        <div className="sm:col-span-2"><Err msg={err} /><button className={btn} type="submit">Save</button></div>
      </form>
    </Modal>
  );
}

// ── Calculators ───────────────────────────────────────────────────────────────
function CalcTab({ meta }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <CoverageCalc meta={meta} />
      <PriceCalc meta={meta} />
      <div className="lg:col-span-2"><LandedCalc meta={meta} /></div>
    </div>
  );
}

function Card({ title, children }) {
  return <section className="rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900"><h2 className="mb-3 text-sm font-semibold">{title}</h2>{children}</section>;
}

function useCalc(path) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const seq = useRef(0);
  const run = useCallback(async (body) => {
    // Only the latest request may update the result (responses can arrive out of order).
    const mine = ++seq.current;
    try {
      const r = await api.post(path, body);
      if (mine === seq.current) { setRes(r); setErr(''); }
    } catch (e) { if (mine === seq.current) { setRes(null); setErr(e.message); } }
  }, [path]);
  return [res, err, run];
}

function CoverageCalc({ meta }) {
  const [f, setF] = useState({ area: '10000', areaUnit: 'sf', preset: 'grey_fibre_1_6', dosage: '', waste_pct: String(meta.constants.waste_pct), th_lifts: '1' });
  const [res, err, run] = useCalc('/catalog/calc/coverage');
  const [th, thErr, runTh] = useCalc('/catalog/calc/trowel-hard');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    const area = f.areaUnit === 'sf' ? { area_sf: f.area } : { area_m2: f.area };
    run({ ...area, ...(f.dosage ? { dosage_lb_sf: f.dosage } : { preset: f.preset }), waste_pct: f.waste_pct });
    runTh({ ...area, lifts: f.th_lifts, waste_pct: f.waste_pct });
  }, [f, run, runTh]);
  return (
    <Card title="Coverage: area → pounds, bags, pallets">
      <div className="grid grid-cols-2 gap-2">
        <L t="Area"><div className="flex gap-1"><input className={input} inputMode="decimal" value={f.area} onChange={set('area')} /><select className={`${input} w-20`} value={f.areaUnit} onChange={set('areaUnit')}><option value="sf">sf</option><option value="m2">m²</option></select></div></L>
        <L t="Dosage preset"><select className={input} value={f.preset} onChange={set('preset')}>{meta.dosage_presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select></L>
        <L t="Or dosage lb/sf"><input className={input} inputMode="decimal" value={f.dosage} onChange={set('dosage')} placeholder="uses preset" /></L>
        <L t="Waste %"><input className={input} inputMode="decimal" value={f.waste_pct} onChange={set('waste_pct')} /></L>
      </div>
      <Err msg={err} />
      {res && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
          <Field label="Dry shake" value={`${fmt(res.total_lb)} lb · ${fmt(res.total_kg)} kg`} />
          <Field label={`Bags (${fmt(res.bag_kg)} kg)`} value={fmt(res.bags)} />
          <Field label={`Pallets (${res.bags_per_pallet}/pallet)`} value={`${res.pallets} (${res.full_pallets} full + ${res.loose_bags} ${countWord(res.loose_bags, 'bag')})`} />
          <Field label="Dosage" value={`${fmt(res.dosage_lb_sf, 2)} lb/sf · ${fmt(res.dosage_kg_m2, 2)} kg/m²`} />
          <Field label="Area" value={`${fmt(res.area_sf)} sf · ${fmt(res.area_m2)} m²`} />
          {res.passes && <Field label="Passes" value={res.passes.map((p) => `${Math.round(p.share * 100)}%: ${fmt(p.lb)} lb`).join(' · ')} />}
        </div>
      )}
      <div className="mt-4 border-t border-[var(--border)] pt-3">
        <div className="flex items-end gap-2">
          <L t="Trowel Hard lifts"><input className={`${input} w-20`} inputMode="numeric" value={f.th_lifts} onChange={set('th_lifts')} /></L>
          <p className="pb-2 text-sm">{th ? <>≈ <b>{fmt(th.gallons, 1)} gal</b> ({fmt(th.liters)} L) at {th.sf_per_gal} sf/gal per lift</> : null}</p>
        </div>
        <Err msg={thErr} />
      </div>
    </Card>
  );
}

function PriceCalc() {
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ product_id: '', mode: 'area', area_sf: '10000', dosage_lb_sf: '1.6', waste_pct: '5', qty_units: '10', markup_pct: '', add_per_unit: '' });
  const [res, err, run] = useCalc('/catalog/calc/price');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { api.get('/catalog/products').then((r) => setProducts(r.products || [])).catch(() => {}); }, []);
  const prod = products.find((p) => p.id === f.product_id);
  const byWeight = prod && ['kg', 'lb'].includes(prod.unit_qty_uom);
  function go(e) {
    e.preventDefault();
    const body = { product_id: f.product_id, markup_pct: f.markup_pct || undefined, add_per_unit: f.add_per_unit || undefined };
    if (byWeight && f.mode === 'area') Object.assign(body, { area_sf: f.area_sf, dosage_lb_sf: f.dosage_lb_sf, waste_pct: f.waste_pct });
    else body.qty_units = f.qty_units;
    run(body);
  }
  return (
    <Card title="Sell price: cost + markup, margin and floor">
      <form onSubmit={go} className="grid grid-cols-2 gap-2">
        <L t="Product" wide><select className={input} value={f.product_id} onChange={set('product_id')} required>
          <option value="">Choose…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}{p.cost?.status !== 'known' ? ' (cost unknown)' : ''}</option>)}
        </select></L>
        {byWeight && <L t="Price by"><select className={input} value={f.mode} onChange={set('mode')}><option value="area">area at dosage</option><option value="units">units</option></select></L>}
        {byWeight && f.mode === 'area' ? (<>
          <L t="Area (sf)"><input className={input} inputMode="decimal" value={f.area_sf} onChange={set('area_sf')} /></L>
          <L t="Dosage (lb/sf)"><input className={input} inputMode="decimal" value={f.dosage_lb_sf} onChange={set('dosage_lb_sf')} /></L>
          <L t="Waste %"><input className={input} inputMode="decimal" value={f.waste_pct} onChange={set('waste_pct')} /></L>
        </>) : <L t={`Quantity (${prod?.sale_unit || 'units'})`}><input className={input} inputMode="decimal" value={f.qty_units} onChange={set('qty_units')} /></L>}
        <L t="Markup % (blank = default)"><input className={input} inputMode="decimal" value={f.markup_pct} onChange={set('markup_pct')} /></L>
        <L t={`Landed add-on $ per ${prod?.sale_unit || 'unit'} (freight, duty…)`}><input className={input} inputMode="decimal" value={f.add_per_unit} onChange={set('add_per_unit')} /></L>
        <div className="col-span-2"><button className={btn} type="submit" disabled={!f.product_id}>Price it</button></div>
      </form>
      <Err msg={err} />
      {res && res.status !== 'known' && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{res.warnings.join(' ')}</p>}
      {res && res.status === 'known' && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Field label="Markup / margin" value={`${res.markup_pct}% / ${res.margin_pct}%`} />
            {res.sell_per_sf != null && <Field label="Sell per sf (cost)" value={`${usd(res.sell_per_sf, 4)} (${usd(res.cost_per_sf, 4)})`} />}
            {res.sell_per_lb != null && <Field label="Sell per lb (cost)" value={`${usd(res.sell_per_lb, 4)} (${usd(res.cost_per_lb, 4)})`} />}
            {res.sell_per_unit != null && <Field label="Sell per unit (cost)" value={`${usd(res.sell_per_unit)} (${usd(res.cost_per_unit)})`} />}
            {res.sell_per_gallon != null && <Field label="Sell per gal" value={usd(res.sell_per_gallon)} />}
            {res.extended_sell != null && <Field label={`Extended (${fmt(res.units)} units)`} value={`${usd(res.extended_sell)} · profit ${usd(res.extended_sell - res.extended_cost)}`} />}
          </div>
          {[...res.floor_checks, ...res.warnings.map((w) => ({ severity: 'info', message: w }))].map((c) => (
            <p key={c.message} className={`flex items-start gap-1.5 text-xs ${c.severity === 'block' ? 'text-red-600' : 'text-amber-700 dark:text-amber-400'}`}><AlertTriangle size={12} className="mt-0.5 shrink-0" />{c.message}</p>
          ))}
        </div>
      )}
    </Card>
  );
}

const newLine = () => ({ label: '', qty: '', unit_cost_usd: '', weight_lb: '', area_sf: '' });
const newCharge = (key = 'ocean_freight') => ({ key, basis: 'amount', amount: '', currency: 'USD', pct: '', min_usd: '', max_usd: '' });

function LandedCalc({ meta }) {
  const [lines, setLines] = useState([newLine()]);
  const [charges, setCharges] = useState([newCharge('ocean_freight'), newCharge('duty'), newCharge('broker'), newCharge('pinnacle_handling'), newCharge('domestic_truck')]);
  const [eur, setEur] = useState('');
  const [allocation, setAllocation] = useState('weight');
  const [name, setName] = useState('');
  const [saved, setSaved] = useState('');
  const [res, err, run] = useCalc('/catalog/calc/landed');
  const body = () => ({
    lines: lines.filter((l) => l.qty !== ''),
    charges: charges.filter((c) => (c.basis === 'amount' ? c.amount !== '' : c.pct !== '')).map((c) => (c.basis === 'amount'
      ? { key: c.key, basis: 'amount', amount: c.amount, currency: c.currency }
      : { key: c.key, basis: 'pct_goods', pct: c.pct, min_usd: c.min_usd || undefined, max_usd: c.max_usd || undefined })),
    fx: eur ? { EUR: eur } : {}, allocation,
  });
  async function save() {
    try { await api.post('/catalog/landed-estimates', { name, inputs: body() }); setSaved(`Saved "${name}".`); } catch (e) { setSaved(e.message); }
  }
  const upd = (arr, setArr, i, k) => (e) => setArr(arr.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  return (
    <Card title="Landed cost: material + freight, duty/MPF, broker, Pinnacle, truck">
      <p className="mb-2 text-xs text-[var(--text-secondary)]">Enter this shipment's actual quotes. Nothing is assumed: charges left blank are not counted.</p>
      <h3 className="text-xs font-semibold">Goods</h3>
      <div className="overflow-x-auto"><table className="w-full min-w-[560px] text-xs"><thead><tr className="text-left text-[var(--text-secondary)]"><th>Item</th><th>Qty</th><th>Unit cost $</th><th>Lb/unit</th><th>Covers sf</th><th /></tr></thead><tbody>
        {lines.map((l, i) => (
          <tr key={i}>
            {['label', 'qty', 'unit_cost_usd', 'weight_lb', 'area_sf'].map((k) => <td key={k} className="pr-1 py-0.5"><input className={input} value={l[k]} onChange={upd(lines, setLines, i, k)} /></td>)}
            <td><button onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Remove"><Trash2 size={13} /></button></td>
          </tr>
        ))}
      </tbody></table></div>
      <button className={`${btn2} mt-1`} onClick={() => setLines([...lines, newLine()])}>+ goods line</button>
      <h3 className="mt-3 text-xs font-semibold">Charges</h3>
      <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-xs"><tbody>
        {charges.map((c, i) => (
          <tr key={i}>
            <td className="pr-1 py-0.5"><select className={input} value={c.key} onChange={upd(charges, setCharges, i, 'key')}>{meta.charge_keys.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}</select></td>
            <td className="pr-1"><select className={input} value={c.basis} onChange={upd(charges, setCharges, i, 'basis')}><option value="amount">amount</option><option value="pct_goods">% of goods</option></select></td>
            {c.basis === 'amount' ? (<>
              <td className="pr-1"><input className={input} placeholder="amount" value={c.amount} onChange={upd(charges, setCharges, i, 'amount')} /></td>
              <td className="pr-1"><select className={input} value={c.currency} onChange={upd(charges, setCharges, i, 'currency')}><option>USD</option><option>EUR</option></select></td>
            </>) : (<>
              <td className="pr-1"><input className={input} placeholder="%" value={c.pct} onChange={upd(charges, setCharges, i, 'pct')} /></td>
              <td className="pr-1"><div className="flex gap-1"><input className={input} placeholder="min $" value={c.min_usd} onChange={upd(charges, setCharges, i, 'min_usd')} /><input className={input} placeholder="max $" value={c.max_usd} onChange={upd(charges, setCharges, i, 'max_usd')} /></div></td>
            </>)}
            <td><button onClick={() => setCharges(charges.filter((_, j) => j !== i))} aria-label="Remove"><Trash2 size={13} /></button></td>
          </tr>
        ))}
      </tbody></table></div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <button className={btn2} onClick={() => setCharges([...charges, newCharge('other')])}>+ charge</button>
        <L t="EUR→USD (if any EUR)"><input className={`${input} w-24`} value={eur} onChange={(e) => setEur(e.target.value)} /></L>
        <L t="Allocate by"><select className={input} value={allocation} onChange={(e) => setAllocation(e.target.value)}><option value="weight">weight</option><option value="value">value</option></select></L>
        <button className={btn} onClick={() => run(body())}>Calculate</button>
      </div>
      <Err msg={err} />
      {res && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Field label="Goods" value={usd(res.goods_usd)} />
            <Field label="Charges" value={`${usd(res.charges_usd)} (${res.freight_share_pct}% of landed)`} />
            <Field label="Landed" value={usd(res.landed_usd)} />
            <Field label="Per lb / per sf" value={`${usd(res.per_lb, 4)} / ${res.per_sf != null ? usd(res.per_sf, 4) : '—'}`} />
          </div>
          <div className="overflow-x-auto"><table className="w-full min-w-[600px] text-xs"><thead><tr className="text-left text-[var(--text-secondary)]"><th>Item</th><th className="text-right">Material</th><th className="text-right">+ Charges</th><th className="text-right">Charges/unit</th><th className="text-right">Landed/unit</th><th className="text-right">/lb</th><th className="text-right">/sf</th></tr></thead><tbody>
            {res.lines.map((l, i) => <tr key={i} className="border-t border-[var(--border)]"><td>{l.label}</td><td className="text-right">{usd(l.material_usd)}</td><td className="text-right">{usd(l.charges_usd)}</td><td className="text-right">{l.charges_per_unit != null ? usd(l.charges_per_unit, 4) : '—'}</td><td className="text-right">{usd(l.landed_unit_usd, 4)}</td><td className="text-right">{l.landed_per_lb != null ? usd(l.landed_per_lb, 4) : '—'}</td><td className="text-right">{l.landed_per_sf != null ? usd(l.landed_per_sf, 4) : '—'}</td></tr>)}
          </tbody></table></div>
          <p className="text-xs text-[var(--text-secondary)]">For a product's sell price, enter its own line's "Charges/unit" as the landed add-on per unit (it follows the allocation you chose and the shipping weights you entered).</p>
          <div className="flex flex-wrap items-end gap-2">
            <L t="Save as"><input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vessick Rowe freight 2026-10" /></L>
            <button className={btn2} disabled={!name} onClick={save}>Save estimate</button>
            {saved && <span className="text-xs">{saved}</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── FX ────────────────────────────────────────────────────────────────────────
function FxTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ rate: '', rate_date: new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()), source: '' });
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get('/catalog/fx').then((r) => setRows(r.rates || [])).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  async function add(e) {
    e.preventDefault();
    try { await api.post('/catalog/fx', { base_ccy: 'EUR', quote_ccy: 'USD', ...f }); setF({ ...f, rate: '', source: '' }); setErr(''); load(); } catch (e2) { setErr(e2.message); }
  }
  async function del(id) { try { await api.delete(`/catalog/fx/${id}`); load(); } catch (e) { setErr(e.message); } }
  return (
    <Card title="EUR → USD rates (entered by hand, dated)">
      <form onSubmit={add} className="flex flex-wrap items-end gap-2">
        <L t="1 EUR = USD"><input className={`${input} w-28`} inputMode="decimal" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} required /></L>
        <L t="Date"><input type="date" className={input} value={f.rate_date} onChange={(e) => setF({ ...f, rate_date: e.target.value })} /></L>
        <L t="Source"><input className={input} value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="e.g. Relay wire 9/26, ECB" /></L>
        <button className={btn} type="submit">Add rate</button>
      </form>
      <Err msg={err} />
      <table className="mt-3 w-full text-sm"><tbody>
        {rows.map((r) => <tr key={r.id} className="border-t border-[var(--border)]"><td className="py-1">{r.rate_date}</td><td>{r.base_ccy}→{r.quote_ccy} {r.rate}</td><td className="text-xs text-[var(--text-secondary)]">{r.source}</td><td><button onClick={() => del(r.id)} aria-label="Delete"><Trash2 size={13} /></button></td></tr>)}
        {!rows.length && <tr><td className="py-2 text-[var(--text-secondary)]">No rates. EUR costs stay UNKNOWN until a rate is entered.</td></tr>}
      </tbody></table>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">Costs use the latest rate on or before the quote date and warn when it is over 30 days old.</p>
    </Card>
  );
}

// ── Pricing rules ─────────────────────────────────────────────────────────────
function SettingsTab() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  const shown = (p) => ({ ...p, default_markup_pct: p.default_markup_pct ?? '', min_margin_pct: p.min_margin_pct ?? '', tariff_pct: p.tariff_pct ?? '', tariff_source: p.tariff_source ?? '' });
  useEffect(() => { api.get('/catalog/settings').then((r) => setS(shown(r.pricing))).catch((e) => setMsg(e.message)); }, []);
  async function save(e) {
    e.preventDefault();
    try {
      const r = await api.patch('/catalog/settings/pricing', {
        default_markup_pct: s.default_markup_pct, min_margin_pct: s.min_margin_pct === '' ? null : s.min_margin_pct, floor_note: s.floor_note,
        tariff_pct: s.tariff_pct === '' ? null : s.tariff_pct, tariff_source: s.tariff_source === '' ? null : s.tariff_source,
      });
      setS(shown(r.pricing)); setMsg('Saved.');
    } catch (e2) { setMsg(e2.message); }
  }
  if (!s) return <Err msg={msg} />;
  return (
    <Card title="Sell-price rules">
      <form onSubmit={save} className="grid max-w-xl grid-cols-1 gap-3">
        <L t="Default markup on cost (%)"><input className={input} inputMode="decimal" value={s.default_markup_pct} onChange={(e) => setS({ ...s, default_markup_pct: e.target.value })} /></L>
        <L t="Warn when margin is under (%). Blank = only warn below cost"><input className={input} inputMode="decimal" value={s.min_margin_pct} onChange={(e) => setS({ ...s, min_margin_pct: e.target.value })} /></L>
        <L t="Floor note shown with floor warnings"><textarea className={input} rows={3} value={s.floor_note || ''} onChange={(e) => setS({ ...s, floor_note: e.target.value })} /></L>
        <L t="Tariff on Concria material (%). Blank = none. Added to material cost in the quote builder, not to freight or tools; a quote can set its own"><input className={input} inputMode="decimal" value={s.tariff_pct} onChange={(e) => setS({ ...s, tariff_pct: e.target.value })} /></L>
        <L t="Where the tariff comes from (shown on every cost it is on)"><input className={input} value={s.tariff_source} placeholder="e.g. OEC, HTS 3824.50.0050 from Portugal, confirmed 10/14" onChange={(e) => setS({ ...s, tariff_source: e.target.value })} /></L>
        <p className="text-xs text-[var(--text-secondary)]">Per-product floors (e.g. "not below Sika dry-shake") are set on each product.</p>
        <div><button className={btn} type="submit">Save</button> <span className="text-xs">{msg}</span></div>
      </form>
    </Card>
  );
}
